import { useState } from 'react';
import { KeyRound, UserPlus } from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { useSession } from '@/api/session';
import { useCreateUser, useResetPassword, useUpdateUser, useUsers } from '@/api/hooks/users';
import type { OfficeUser } from '@/api/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, Field, Input, Select } from '@/components/ui/Field';
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/Table';
import { toast } from '@/stores/toast';
import { CleanStartCard } from '@/components/settings/CleanStartCard';

const ROLE_NAMES: Record<string, string> = {
  owner: 'Управител',
  dispatcher: 'Диспечер',
  accountant: 'Счетоводство',
  mechanic: 'Майстор',
  driver: 'Шофьор',
  customer: 'Клиент',
};

/** Кого създава управителят оттук. Шофьорите — с код от „Шофьори"; клиентите — от портала. */
const CREATABLE = ['dispatcher', 'accountant', 'mechanic'] as const;

const ROLE_HINT: Record<(typeof CREATABLE)[number], string> = {
  dispatcher: 'Заявки, курсове, разпределение, документи. Без заплати.',
  accountant: 'Фактури, плащания, разходи, справки. Не разпределя курсове.',
  mechanic: 'Само сервизът: проверки, ремонти, части, бус „в сервиз". Без заявки и пари.',
};

const errorText = (error: unknown) => (error instanceof Error ? error.message : undefined);

/**
 * Профилите в офиса. Само управителят: създава диспечер, счетоводител или
 * майстор, сменя паролата им, спира достъпа. Паролата се казва на човека
 * лично — системата не я праща по имейл.
 */
export function UsersPage() {
  const role = useSession((st) => st.user?.role);
  const [showInactive, setShowInactive] = useState(false);
  const users = useUsers(showInactive);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<OfficeUser | null>(null);
  const update = useUpdateUser();

  if (role !== 'owner') return <EmptyState title="Само за управителя." />;
  if (users.isLoading) return <LoadingBlock />;
  if (users.isError) return <ErrorState onRetry={() => void users.refetch()} />;
  const items = (users.data?.items ?? []).filter((u) => u.role !== 'driver' && u.role !== 'customer');

  return (
    <div className="space-y-3 p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-lg font-bold tracking-tight">Потребители</h1>
        <span className="text-xs text-ink-dim">Профилите в офиса — шофьорите се свързват с код от „Шофьори".</span>
        <span className="ml-auto flex items-center gap-3">
          <Checkbox label="И спрените" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          <Button variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setCreating(true)}>
            Нов потребител
          </Button>
        </span>
      </div>

      <Card>
        <CardHeader title="Офис" subtitle={`${items.length} профила`} />
        {items.length === 0 ? (
          <EmptyState title="Няма профили." />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Име</TH>
                <TH>Роля</TH>
                <TH>Имейл</TH>
                <TH>Последно влизане</TH>
                <TH>Достъп</TH>
                <TH />
              </tr>
            </THead>
            <TBody>
              {items.map((user) => (
                <TR key={user.id}>
                  <TD className="font-medium">{user.fullName}</TD>
                  <TD>{ROLE_NAMES[user.role] ?? user.role}</TD>
                  <TD className="text-ink-dim">{user.email ?? '—'}</TD>
                  <TD className="whitespace-nowrap text-ink-dim">{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'никога'}</TD>
                  <TD>{user.isActive ? <Badge tone="ok">активен</Badge> : <Badge tone="neutral">спрян</Badge>}</TD>
                  <TD className="text-right">
                    {user.role !== 'owner' ? (
                      <span className="flex justify-end gap-1.5">
                        <Button size="sm" variant="ghost" icon={<KeyRound className="size-3.5" />} onClick={() => setResetting(user)}>
                          Нова парола
                        </Button>
                        <Button
                          size="sm"
                          variant={user.isActive ? 'danger' : 'secondary'}
                          loading={update.isPending}
                          onClick={() => {
                            if (user.isActive && !window.confirm(`Да спра ли достъпа на ${user.fullName}?`)) return;
                            update.mutate(
                              { id: user.id, isActive: !user.isActive },
                              {
                                onSuccess: () => toast.ok(user.isActive ? 'Достъпът е спрян' : 'Достъпът е върнат', user.fullName),
                                onError: (error) => toast.error('Не стана', errorText(error)),
                              },
                            );
                          }}
                        >
                          {user.isActive ? 'Спри' : 'Върни'}
                        </Button>
                      </span>
                    ) : null}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <CleanStartCard />

      <CreateUserDialog open={creating} onClose={() => setCreating(false)} />
      <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />
    </div>
  );
}

function CreateUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateUser();
  const [form, setForm] = useState({ fullName: '', email: '', role: 'mechanic' as (typeof CREATABLE)[number], password: '' });
  const valid = form.fullName.trim().length >= 3 && /.+@.+\..+/.test(form.email) && form.password.length >= 12;
  const close = () => {
    setForm({ fullName: '', email: '', role: 'mechanic', password: '' });
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title="Нов потребител"
      description="Паролата казвате на човека лично. Ако я забрави — „Нова парола“ от този списък."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Отказ
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!valid}
            onClick={() =>
              create.mutate(
                { fullName: form.fullName.trim(), email: form.email.trim(), role: form.role, password: form.password },
                {
                  onSuccess: (user) => {
                    toast.ok('Профилът е създаден', `${user.fullName} · ${ROLE_NAMES[user.role]}`);
                    close();
                  },
                  onError: (error) => toast.error('Профилът не е създаден', errorText(error)),
                },
              )
            }
          >
            Създай
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Роля" hint={ROLE_HINT[form.role]}>
          <Select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as (typeof CREATABLE)[number] })}
            options={CREATABLE.map((r) => ({ value: r, label: ROLE_NAMES[r]! }))}
          />
        </Field>
        <Field label="Име и фамилия" required>
          <Input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        </Field>
        <Field label="Имейл (за вход)" required>
          <Input type="email" autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Първоначална парола" required hint="Поне 12 знака, с буква и цифра.">
          <Input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }: { user: OfficeUser | null; onClose: () => void }) {
  const reset = useResetPassword();
  const [password, setPassword] = useState('');
  if (!user) return null;
  const close = () => {
    setPassword('');
    onClose();
  };
  return (
    <Dialog
      open
      onClose={close}
      title={`Нова парола за ${user.fullName}`}
      description="Старата спира да важи веднага, а отворените сесии се затварят."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Отказ
          </Button>
          <Button
            variant="primary"
            loading={reset.isPending}
            disabled={password.length < 12}
            onClick={() =>
              reset.mutate(
                { id: user.id, newPassword: password },
                {
                  onSuccess: () => {
                    toast.ok('Паролата е сменена', user.fullName);
                    close();
                  },
                  onError: (error) => toast.error('Паролата не е сменена', errorText(error)),
                },
              )
            }
          >
            Смени
          </Button>
        </>
      }
    >
      <Field label="Нова парола" hint="Поне 12 знака, с буква и цифра.">
        <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
    </Dialog>
  );
}
